import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import SEO from "@/components/SEO";
import { useReveals } from "@/components/Reveal";

const AREAS = [
  {
    id: "logistics",
    kicker: "Area 01",
    title: "Logistics & Maritime",
    intro:
      "A one-stop-shop for shipping — ocean freight, land transportation, warehousing, global logistics, port and container services, delivered across a network spanning over 100 countries.",
    items: [
      { name: "Ocean Freight", desc: "Full container load (FCL) and less than container load (LCL) shipping solutions for maritime cargo — across global trade routes." },
      { name: "Land Transportation", desc: "Door-to-door delivery with reliable trucking and rail freight across continents." },
      { name: "Warehousing", desc: "Storage facilities with inventory management and distribution services." },
      { name: "Global Logistics", desc: "End-to-end supply chain solutions connecting businesses to markets worldwide." },
      { name: "Port Services", desc: "Comprehensive port handling, customs clearance and documentation services." },
      { name: "Container Services", desc: "Container leasing, maintenance and specialized container solutions." },
    ],
  },
  {
    id: "technology",
    kicker: "Area 02",
    title: "Technology & Software",
    intro:
      "The software division builds applications, systems and tools that optimize operations, enhance productivity and drive growth — reliable, scalable and future-ready.",
    items: [
      { name: "Custom Software Development", desc: "Tailored software solutions designed to enhance business processes and deliver innovative tools that add value to operations." },
      { name: "Process Automation & Integration", desc: "Automation systems that accelerate operations and minimize errors, seamlessly integrating existing software for optimal efficiency." },
      { name: "Digital Transformation & Consulting", desc: "Strategic technology solutions supporting the digital transformation journey — preparing businesses for a future-ready approach." },
    ],
  },
];

function CapabilityArea({ area }) {
  const [open, setOpen] = useState(0);
  return (
    <section className="border-t border-white/10 px-[6vw] py-20 md:py-28" data-testid={`capability-area-${area.id}`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber" data-reveal>
        {area.kicker}
      </p>
      <h2 className="mt-6 font-display text-4xl font-black uppercase tracking-tighter md:text-6xl" data-reveal>
        {area.title}
      </h2>
      <p className="mt-6 max-w-2xl text-sm leading-relaxed text-smoke md:text-base" data-reveal>
        {area.intro}
      </p>

      <div className="mt-14 border-t border-white/10" data-reveal>
        {area.items.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={item.name} className="border-b border-white/10">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="group flex w-full items-center justify-between gap-6 py-6 text-left md:py-8"
                data-testid={`cap-toggle-${area.id}-${i}`}
                aria-expanded={isOpen}
              >
                <span className="flex items-baseline gap-5 md:gap-8">
                  <span className="font-mono text-xs text-smoke">0{i + 1}</span>
                  <span
                    className={`font-display text-2xl font-bold uppercase tracking-tight transition-colors duration-300 md:text-4xl ${
                      isOpen ? "text-amber" : "group-hover:text-amber"
                    }`}
                  >
                    {item.name}
                  </span>
                </span>
                <Plus
                  className={`h-5 w-5 shrink-0 text-smoke transition-transform duration-300 ${isOpen ? "rotate-45 text-amber" : ""}`}
                />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <p className="max-w-2xl pb-8 pl-9 text-sm leading-relaxed text-smoke md:pl-16 md:text-base">
                      {item.desc}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function Capabilities() {
  useReveals();
  return (
    <main data-testid="capabilities-page">
      <SEO
        title="Capabilities — OA Holding CO LTD."
        description="OA Holding capabilities: ocean freight, land transportation, warehousing, global logistics, port and container services — plus custom software development, process automation and digital transformation."
        path="/capabilities"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "OA Holding Capabilities",
          url: "https://oaholding.co/capabilities",
        }}
      />
      <section className="px-[6vw] pb-16 pt-[22vh] md:pt-[26vh]">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber md:text-xs" data-reveal>
          Capabilities
        </p>
        <h1 className="mt-6 font-display text-[12vw] font-black uppercase leading-[0.85] tracking-tighter md:text-[8vw]" data-reveal>
          What OA
          <br />
          moves.
        </h1>
        <p className="mt-10 max-w-2xl font-display text-xl font-bold leading-snug tracking-tight text-bone/90 md:text-3xl" data-reveal>
          Goods across oceans.{" "}
          <span className="text-amber">Information across systems.</span>
        </p>
      </section>

      {AREAS.map((area) => (
        <CapabilityArea key={area.id} area={area} />
      ))}
    </main>
  );
}
